import { ImageToCadModel } from '@shared/types';

// Mesh generation constants

// Polygon count limits
export const POLYGON_COUNTS = {
  // Topology defaults
  QUADS_DEFAULT: 100000,
  POLYS_DEFAULT: 100000,

  // Model-specific maximums
  STANDARD_MAX: 0, // SAM 3D doesn't support polygon limits
  ULTRA_MAX: 300000, // Meshy v6 API limit is 300k
  TEXTURELESS_MAX: 50000,
  TRELLIS2_MAX: 500000, // Trellis 2 decimation_target default/limit is 500k vertices
  HUNYUAN_MAX: 1500000, // Hunyuan 3D v3.1 Pro face_count max is 1.5M

  // Model-specific defaults
  STANDARD_DEFAULT: 0, // SAM 3D handles this automatically
  TEXTURELESS_DEFAULT: 50000,
  ULTRA_DEFAULT: 300000, // Set to max for ultra quality
  TRELLIS2_DEFAULT: 500000, // Trellis 2 decimation_target default
  HUNYUAN_DEFAULT: 500000, // Hunyuan 3D v3.1 Pro face_count default

  // UI limits
  MIN_POLYGON_COUNT: 1000,
} as const;

// Material defaults
export const MATERIAL_DEFAULTS = {
  BRIGHTNESS: 50 as number,
  BRIGHTNESS_TEXTURELESS: 100 as number,
  BRIGHTNESS_UPSCALED: 50 as number, // Upscaled models use standard brightness
  ROUGHNESS: 100 as number,
  NORMAL_INTENSITY: 0 as number,
};

// Model-specific configuration
export interface ModelConfig {
  brightness: number;
  roughness: number;
  normalIntensity: number;
  polygonCount: {
    quads: number;
    polys: number;
  };
  showPolygonControls: boolean;
  showNormalIntensity: boolean;
  maxPolygonCount?: number; // For server-side limits
}

export const MODEL_CONFIGS: Record<ImageToCadModel, ModelConfig> = {
  fast: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS_TEXTURELESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TEXTURELESS_DEFAULT,
      polys: POLYGON_COUNTS.TEXTURELESS_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: false,
    maxPolygonCount: POLYGON_COUNTS.TEXTURELESS_MAX,
  },
  quality: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.STANDARD_DEFAULT,
      polys: POLYGON_COUNTS.STANDARD_DEFAULT,
    },
    showPolygonControls: false, // SAM 3D doesn't support polygon/quad controls
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.STANDARD_MAX,
  },
  ultra: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.ULTRA_DEFAULT,
      polys: POLYGON_COUNTS.ULTRA_DEFAULT,
    },
    showPolygonControls: true,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.ULTRA_MAX, // Meshy v6 limit is 300k
  },
  trellis2: {
    // High-fidelity textured tier — mirrors ultra (textured GLB, normal map).
    // Polygon controls hidden for v1: Trellis 2's decimation_target is not yet
    // wired to the UI slider; the fal call uses the model default.
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TRELLIS2_DEFAULT,
      polys: POLYGON_COUNTS.TRELLIS2_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.TRELLIS2_MAX, // Trellis 2 decimation_target limit
  },
  trellis2alias: {
    // Second dropdown entry ("Trellis.2") on the SAME fal Trellis 2 backend as
    // `trellis2` — identical material tier by construction.
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TRELLIS2_DEFAULT,
      polys: POLYGON_COUNTS.TRELLIS2_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.TRELLIS2_MAX,
  },
  magneto4b: {
    // Magneto Pixal3D (self-hosted on Modal). Mirrors trellis2's material tier —
    // textured GLB, normal map. Polygon controls hidden for v1: the fork's
    // decimation_target isn't wired to the UI slider yet (uses the model default).
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TRELLIS2_DEFAULT,
      polys: POLYGON_COUNTS.TRELLIS2_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.TRELLIS2_MAX,
  },
  meshy7: {
    // Meshy 7 via fal (`fal-ai/meshy/v7/image-to-3d`). Same vendor and the same
    // remesh limits as the `ultra` tier (Meshy v6 preview), so it mirrors ultra
    // exactly — polygon controls exposed, clamped to the 300k API ceiling.
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.ULTRA_DEFAULT,
      polys: POLYGON_COUNTS.ULTRA_DEFAULT,
    },
    showPolygonControls: true,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.ULTRA_MAX, // Meshy remesh limit is 300k
  },
  hunyuan: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.HUNYUAN_DEFAULT,
      polys: POLYGON_COUNTS.HUNYUAN_DEFAULT,
    },
    showPolygonControls: true,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.HUNYUAN_MAX, // Hunyuan v3.1 Pro face_count max is 1.5M
  },
  tripo: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TRELLIS2_DEFAULT,
      polys: POLYGON_COUNTS.TRELLIS2_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.TRELLIS2_MAX,
  },
  trellis1: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TRELLIS2_DEFAULT,
      polys: POLYGON_COUNTS.TRELLIS2_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.TRELLIS2_MAX,
  },
  pixal3d: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TRELLIS2_DEFAULT,
      polys: POLYGON_COUNTS.TRELLIS2_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.TRELLIS2_MAX,
  },
  meshy5: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.ULTRA_DEFAULT,
      polys: POLYGON_COUNTS.ULTRA_DEFAULT,
    },
    showPolygonControls: true,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.ULTRA_MAX,
  },
  meshy6: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.ULTRA_DEFAULT,
      polys: POLYGON_COUNTS.ULTRA_DEFAULT,
    },
    showPolygonControls: true,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.ULTRA_MAX,
  },
  hunyuan_v2: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TRELLIS2_DEFAULT,
      polys: POLYGON_COUNTS.TRELLIS2_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.TRELLIS2_MAX,
  },
  hunyuan_v2_mini: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TRELLIS2_DEFAULT,
      polys: POLYGON_COUNTS.TRELLIS2_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.TRELLIS2_MAX,
  },
  hunyuan_v3: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.HUNYUAN_DEFAULT,
      polys: POLYGON_COUNTS.HUNYUAN_DEFAULT,
    },
    showPolygonControls: true,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.HUNYUAN_MAX,
  },
  hunyuan_rapid: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TRELLIS2_DEFAULT,
      polys: POLYGON_COUNTS.TRELLIS2_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.TRELLIS2_MAX,
  },
  tripo_h31: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TRELLIS2_DEFAULT,
      polys: POLYGON_COUNTS.TRELLIS2_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.TRELLIS2_MAX,
  },
  tripo_p1: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TRELLIS2_DEFAULT,
      polys: POLYGON_COUNTS.TRELLIS2_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: true,
    maxPolygonCount: POLYGON_COUNTS.TRELLIS2_MAX,
  },
  triposr: {
    brightness: MATERIAL_DEFAULTS.BRIGHTNESS_TEXTURELESS,
    roughness: MATERIAL_DEFAULTS.ROUGHNESS,
    normalIntensity: MATERIAL_DEFAULTS.NORMAL_INTENSITY,
    polygonCount: {
      quads: POLYGON_COUNTS.TEXTURELESS_DEFAULT,
      polys: POLYGON_COUNTS.TEXTURELESS_DEFAULT,
    },
    showPolygonControls: false,
    showNormalIntensity: false,
    maxPolygonCount: POLYGON_COUNTS.TEXTURELESS_MAX,
  },
};

// Helper functions for model configuration
export const getModelConfig = (model: ImageToCadModel): ModelConfig => {
  return MODEL_CONFIGS[model];
};

export const getModelDefaultBrightness = (model: ImageToCadModel): number => {
  return MODEL_CONFIGS[model].brightness;
};

export const getModelDefaultPolygonCount = (
  model: ImageToCadModel,
  topology: 'quads' | 'polys',
): number => {
  return MODEL_CONFIGS[model].polygonCount[topology];
};

export const shouldShowPolygonControls = (model: ImageToCadModel): boolean => {
  return MODEL_CONFIGS[model].showPolygonControls;
};

export const shouldShowNormalIntensity = (model: ImageToCadModel): boolean => {
  return MODEL_CONFIGS[model].showNormalIntensity;
};

export const getMaxPolygonCount = (
  model: ImageToCadModel,
  _topology: 'quads' | 'polys',
): number => {
  // Return model-specific max, topology doesn't affect the limit
  return MODEL_CONFIGS[model].maxPolygonCount || POLYGON_COUNTS.STANDARD_MAX;
};

// Legacy exports for backward compatibility
export const DEFAULT_BRIGHTNESS = MATERIAL_DEFAULTS.BRIGHTNESS;
export const DEFAULT_BRIGHTNESS_UPSCALED =
  MATERIAL_DEFAULTS.BRIGHTNESS_UPSCALED;
export const DEFAULT_ROUGHNESS = MATERIAL_DEFAULTS.ROUGHNESS;
export const DEFAULT_NORMAL_INTENSITY = MATERIAL_DEFAULTS.NORMAL_INTENSITY;
